import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Receipt, Loader2, Trash2, Download, ImagePlus, X, Share2, Sparkles } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import type { SellerExpense } from "@shared/schema";
import { EXPENSE_CATEGORIES, expenseCategoryLabel, buildExpensesCsv } from "@shared/expenses";
import { MileageTracker } from "@/components/MileageTracker";

type ExpensesResponse = {
  expenses: SellerExpense[];
  totalExpenses: number;
  salesIncome: number;
};

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;

async function resizeImageToDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

function fmt(amount: number): string {
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export default function SellerExpenses() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const [expenseDate, setExpenseDate] = useState(today);
  const [category, setCategory] = useState("seeds");
  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [receipt, setReceipt] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);

  const { data, isLoading } = useQuery<ExpensesResponse>({
    queryKey: ["/api/seller/expenses"],
    queryFn: async () => {
      const res = await fetch("/api/seller/expenses", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const expenses = data?.expenses ?? [];
  const totalExpenses = data?.totalExpenses ?? 0;
  const salesIncome = data?.salesIncome ?? 0;
  const net = salesIncome - totalExpenses;

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/seller/expenses", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          expenseDate,
          category,
          vendor: vendor.trim() || null,
          amount,
          notes: notes.trim() || null,
          receiptImage: receipt,
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not save expense");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/seller/expenses"] });
      setVendor("");
      setAmount("");
      setNotes("");
      setReceipt(null);
      setExpenseDate(today);
      setCategory("seeds");
      toast({ title: "Expense logged" });
    },
    onError: (err: any) => {
      toast({ title: "Could not save", description: err?.message ?? "Try again", variant: "destructive" });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/seller/expenses/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/seller/expenses"] }),
    onError: (err: any) =>
      toast({ title: "Could not delete", description: err?.message ?? "Try again", variant: "destructive" }),
  });

  const handleAttach = async (file: File | undefined) => {
    if (!file) return;
    setAttaching(true);
    try {
      setReceipt(await resizeImageToDataUrl(file));
    } catch (err: any) {
      toast({
        title: "Could not attach receipt",
        description: err?.message || "Try a JPEG or PNG photo.",
        variant: "destructive",
      });
    } finally {
      setAttaching(false);
    }
  };

  const [scanning, setScanning] = useState(false);

  // Snap a receipt → attach it AND let AI fill in the form fields.
  const handleScan = async (file: File | undefined) => {
    if (!file) return;
    setScanning(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      setReceipt(dataUrl);
      const imageBase64 = dataUrl.split(",")[1] ?? "";
      const res = await fetch("/api/seller/expenses/scan-receipt", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64, mediaType: "image/jpeg" }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not read receipt");
      const a = (await res.json()) as {
        vendor: string | null;
        amount: number | null;
        expenseDate: string | null;
        category: string;
        notes: string | null;
        confidence: string;
      };
      if (a.vendor) setVendor(a.vendor);
      if (a.amount != null) setAmount(String(a.amount));
      if (a.expenseDate) setExpenseDate(a.expenseDate);
      if (a.category) setCategory(a.category);
      if (a.notes) setNotes(a.notes);
      toast({
        title: a.confidence === "low" ? "Scanned — double-check the details" : "Receipt scanned",
        description: "Review the fields, then tap Add expense.",
      });
    } catch (err: any) {
      toast({
        title: "Could not read receipt",
        description: err?.message || "Enter the details manually.",
        variant: "destructive",
      });
    } finally {
      setScanning(false);
    }
  };

  const [exporting, setExporting] = useState(false);

  const downloadCsv = () => {
    const csv = buildExpensesCsv(expenses, salesIncome, totalExpenses);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stoop-expenses-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const emailCsv = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/seller/expenses/email-csv", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not send email");
      return res.json() as Promise<{ to: string }>;
    },
    onSuccess: (r) => toast({ title: "Spreadsheet sent", description: `Emailed to ${r.to}` }),
    onError: (err: any) =>
      toast({ title: "Could not email", description: err?.message ?? "Try again", variant: "destructive" }),
  });

  const isNative = Capacitor.isNativePlatform();
  const canNativeShare =
    isNative && Capacitor.isPluginAvailable("Filesystem") && Capacitor.isPluginAvailable("Share");

  const handleExport = async () => {
    // Web: a normal browser download just works.
    if (!isNative) {
      downloadCsv();
      return;
    }
    // Native build with the file plugins: write the CSV to disk and open
    // the OS share sheet (Files, Mail, Drive, AirDrop…). This is the smooth
    // path once the rebuilt app ships.
    if (canNativeShare) {
      setExporting(true);
      try {
        const csv = buildExpensesCsv(expenses, salesIncome, totalExpenses);
        const written = await Filesystem.writeFile({
          path: `stoop-expenses-${today}.csv`,
          data: csv,
          directory: Directory.Cache,
          encoding: Encoding.UTF8,
        });
        await Share.share({
          title: "Stoop expense spreadsheet",
          text: `Stoop expenses as of ${today}`,
          url: written.uri,
          dialogTitle: "Export expense spreadsheet",
        });
      } catch (err: any) {
        // Share.share rejects when the user dismisses the sheet — not an error.
        if (err?.message && !/cancel/i.test(String(err.message))) {
          toast({ title: "Could not export", description: err.message, variant: "destructive" });
        }
      } finally {
        setExporting(false);
      }
      return;
    }
    // Native app that predates the file plugins: email it instead.
    emailCsv.mutate();
  };

  const exportBusy = exporting || emailCsv.isPending;
  const amountValid = Number(amount) > 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header cartCount={0} onCartClick={() => {}} />
      <main className="flex-1">
        <div className="container mx-auto max-w-2xl px-4 py-5">
          <Button variant="ghost" size="sm" onClick={() => setLocation("/seller")} className="mb-3">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to dashboard
          </Button>

          <div className="mb-1 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-bold">Expenses & write-offs</h1>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            Log what you spend growing or making what you sell. Export a spreadsheet at tax time to write
            these costs off against your Stoop sales income.
          </p>

          <Card className="mb-4">
            <CardContent className="grid grid-cols-3 gap-2 pt-4 text-center">
              <div>
                <div className="text-xs text-muted-foreground">Sales income</div>
                <div className="text-base font-semibold">{fmt(salesIncome)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Expenses</div>
                <div className="text-base font-semibold">{fmt(totalExpenses)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Net (est.)</div>
                <div className="text-base font-semibold">{fmt(net)}</div>
              </div>
            </CardContent>
          </Card>

          <MileageTracker />

          <Card className="mb-4">
            <CardContent className="space-y-3 pt-4">
              <label
                className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-primary/50 bg-primary/5 px-3 py-3 text-sm font-medium text-primary ${
                  scanning ? "opacity-60" : "hover:bg-primary/10"
                }`}
              >
                {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {scanning ? "Reading receipt…" : "Scan a receipt to auto-fill"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={scanning}
                  onChange={(e) => {
                    void handleScan(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </label>
              <p className="-mt-1 text-center text-[11px] text-muted-foreground">
                Snap a photo of your receipt — we'll fill in the details below
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="exp-date" className="text-xs">Date</Label>
                  <Input
                    id="exp-date"
                    type="date"
                    value={expenseDate}
                    max={today}
                    onChange={(e) => setExpenseDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="exp-amount" className="text-xs">Amount ($)</Label>
                  <Input
                    id="exp-amount"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="exp-category" className="text-xs">Category</Label>
                <select
                  id="exp-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {EXPENSE_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="exp-vendor" className="text-xs">Vendor / store (optional)</Label>
                <Input
                  id="exp-vendor"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  placeholder="e.g. Burpee, Lowe's, Amazon"
                  maxLength={200}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="exp-notes" className="text-xs">Notes (optional)</Label>
                <Textarea
                  id="exp-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What was it for?"
                  className="mt-1 min-h-[60px]"
                />
              </div>

              <div>
                <Label className="text-xs">Receipt photo (optional)</Label>
                <div className="mt-1">
                  {receipt ? (
                    <div className="relative inline-block">
                      <img src={receipt} alt="Receipt" className="h-20 w-20 rounded-md border object-cover" />
                      <button
                        type="button"
                        onClick={() => setReceipt(null)}
                        className="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-background text-foreground shadow-sm ring-1 ring-border"
                        aria-label="Remove receipt"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="inline-flex h-20 w-20 cursor-pointer items-center justify-center rounded-md border border-dashed text-muted-foreground hover:bg-muted">
                      {attaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          void handleAttach(e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              <Button
                className="w-full"
                disabled={create.isPending || !amountValid}
                onClick={() => {
                  if (!amountValid) {
                    toast({ title: "Enter an amount", variant: "destructive" });
                    return;
                  }
                  create.mutate();
                }}
              >
                {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Add expense
              </Button>
            </CardContent>
          </Card>

          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Logged expenses</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={expenses.length === 0 || exportBusy}
            >
              {exportBusy ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : isNative ? (
                <Share2 className="mr-1 h-4 w-4" />
              ) : (
                <Download className="mr-1 h-4 w-4" />
              )}
              {isNative ? "Share spreadsheet" : "Download CSV"}
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : expenses.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No expenses logged yet. Add your first one above.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {expenses.map((e) => (
                <Card key={e.id}>
                  <CardContent className="flex items-center gap-3 py-3">
                    {e.receiptImageUrl ? (
                      <a href={e.receiptImageUrl} target="_blank" rel="noreferrer">
                        <img src={e.receiptImageUrl} alt="Receipt" className="h-12 w-12 rounded-md border object-cover" />
                      </a>
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-md border bg-muted text-muted-foreground">
                        <Receipt className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-medium">{expenseCategoryLabel(e.category)}</span>
                        <span className="shrink-0 text-sm font-semibold">{fmt(Number(e.amount))}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {e.expenseDate}
                        {e.vendor ? ` · ${e.vendor}` : ""}
                        {e.miles != null ? ` · ${Number(e.miles).toFixed(1)} mi` : ""}
                      </div>
                      {e.notes ? <div className="truncate text-xs text-muted-foreground">{e.notes}</div> : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => remove.mutate(e.id)}
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      aria-label="Delete expense"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
